import { type ReactNode } from 'react';
import { Badge, Box, Checkbox, Flex, Text, theme } from '@optiaxiom/react';

/**
 * A small renderer for sticky cards that are interactive checklists.
 *
 * Proteus renders both `Switch` and `Input type="checkbox"` as a toggle — it has
 * no square-checkbox visual — so a glyph checklist (rewritten by
 * `transformChecklist`) is rendered here instead, with Axiom's real `Checkbox`.
 * It covers the handful of node types a checklist card uses (Group/Text/Heading/
 * Badge/Separator + the checkbox Input); anything else falls back to its children
 * or nothing. Non-checklist cards still go through ProteusDocumentRenderer.
 */

type Node = Record<string, unknown>;

const BADGE_INTENTS = new Set([
  'information',
  'success',
  'warning',
  'danger',
  'neutral',
  'primary',
]);

type Ctx = {
  data: Record<string, unknown>;
  onToggle: (name: string, checked: boolean) => void;
};

function badgeIntent(
  intent: unknown
): 'information' | 'success' | 'warning' | 'danger' | 'neutral' | 'primary' {
  return typeof intent === 'string' && BADGE_INTENTS.has(intent)
    ? (intent as 'information')
    : 'neutral';
}

function renderNode(node: unknown, ctx: Ctx, key: string | number): ReactNode {
  if (node == null || typeof node === 'boolean') return null;
  if (typeof node !== 'object') return String(node);
  const n = node as Node;
  const kids = Array.isArray(n.children)
    ? n.children.map((c, i) => renderNode(c, ctx, i))
    : (n.children as ReactNode);

  switch (n.$type) {
    case 'Document':
      return Array.isArray(n.body) ? n.body.map((c, i) => renderNode(c, ctx, i)) : null;

    case 'Group':
      return (
        <Flex
          key={key}
          flexDirection={n.flexDirection === 'row' ? 'row' : 'column'}
          gap={(n.gap as never) ?? '4'}
          alignItems={(n.alignItems as never) ?? undefined}
          style={{ minWidth: 0 }}
        >
          {kids}
        </Flex>
      );

    case 'Heading':
      return (
        <Text key={key} fontWeight="600" fontSize="md">
          {kids}
        </Text>
      );

    case 'Text':
      return (
        <Text key={key} fontSize="sm">
          {kids}
        </Text>
      );

    case 'Badge':
      return (
        <Badge key={key} intent={badgeIntent(n.intent)}>
          {kids}
        </Badge>
      );

    case 'Separator':
      return (
        <Box
          key={key}
          style={{ height: 1, background: theme.colors['border.tertiary'], margin: '4px 0' }}
        />
      );

    case 'Input':
      if (n.type === 'checkbox' && typeof n.name === 'string') {
        const name = n.name;
        return (
          <Checkbox
            key={key}
            checked={!!ctx.data[name]}
            onCheckedChange={checked => ctx.onToggle(name, checked)}
          >
            {kids}
          </Checkbox>
        );
      }
      return null;

    default:
      return Array.isArray(n.children) ? (
        <Flex key={key} flexDirection="column" gap="4">
          {kids}
        </Flex>
      ) : null;
  }
}

export function StickyChecklist({
  doc,
  data,
  onToggle,
}: {
  doc: unknown;
  data: Record<string, unknown>;
  onToggle: (name: string, checked: boolean) => void;
}) {
  return (
    <Flex flexDirection="column" gap="8" data-testid="sticky-checklist">
      {renderNode(doc, { data, onToggle }, 'root')}
    </Flex>
  );
}
